import { User, UserManager } from 'oidc-client-ts'
import { createContext, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { USER_MANAGER } from './UserManager'
import * as Sentry from '@sentry/browser'

export const InternalRoleId = '594cd817-6de7-4575-b8ee-f93860db0cf8'
export const SupplierRoleId = '911d0007-8805-4d2c-a338-c9aad3e337b2'
export const AdminRoleId = '66a86ef7-2a61-489b-b5c0-bfe617527385'
export const ManagerRoleId = '1625be50-89e9-4aab-97c1-0d59c0f082d2'
export const GuestRoleId = 'c5f44132-0b33-4372-bcfb-d00ff46ee695'
export const CustomerServiceRoleId = '58d60258-8512-421e-aa8b-11c570ebff65'
export const MandatorySignIn = false
export const Home = '/'

export interface UserContext {
  id: string
  email?: string
  permissions: string[]
  firstName?: string
  lastName?: string
  displayName?: string
  roles: string[]
  locale?: string
  isAdmin: boolean
  isInternalUser: boolean
  is4UPro: boolean
  isManager: boolean
  isSupplier: boolean
  isCustomerService: boolean
  isGuest: boolean
  tenant?: string
  tags: string[]
  updatedAt?: Date
}

export interface AuthContext {
  manager: UserManager
  user: UserContext | null | undefined
}

export const AuthContext = createContext<AuthContext>({
  manager: USER_MANAGER,
  user: undefined,
})

function decodeUserProfile(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>
  } catch {
    return {}
  }
}

function getUserContext(user: User): UserContext {
  const profile = decodeUserProfile(user.access_token)

  const permission = profile.permission
  const tag = user.profile.tag
  const role = profile.role
  const locale = user.profile.locale?.split('-').at(0)

  const permissions = Array.isArray(permission)
    ? (permission as string[])
    : typeof permission === 'string'
      ? [permission]
      : []
  const tags = Array.isArray(tag) ? (tag as string[]) : typeof tag === 'string' ? [tag] : []
  const roles = Array.isArray(role) ? (role as string[]) : typeof role === 'string' ? [role] : []

  return {
    id: profile.sub as string,
    email: user.profile.email ?? (profile.email as string) ?? (profile.preferred_username as string),
    permissions,
    displayName: user.profile.name ?? (profile.preferred_username as string),
    roles,
    locale,
    isAdmin: roles.includes(AdminRoleId),
    isInternalUser: roles.includes(InternalRoleId) || roles.includes(AdminRoleId),
    is4UPro: tags.some((t) => t.toUpperCase() === 'INTERFLEETPRO'),
    isManager: roles.includes(ManagerRoleId),
    isSupplier: roles.includes(SupplierRoleId),
    isCustomerService: roles.includes(CustomerServiceRoleId),
    isGuest: roles.includes(GuestRoleId),
    tenant: profile.tenant as string,
    tags,
    updatedAt: user.profile.updated_at ? new Date(user.profile.updated_at) : undefined,
  }
}

export function AuthProvider(props: PropsWithChildren) {
  const [authData, setAuthData] = useState<AuthContext>({
    manager: USER_MANAGER,
    user: undefined,
  })

  const initialized = useRef(false)
  const navigate = useNavigate()

  const authStep = () => {
    const url = window.location.href
    if (url.startsWith(USER_MANAGER.settings.redirect_uri)) {
      return 'signIn'
    } else if (url.startsWith(USER_MANAGER.settings.post_logout_redirect_uri!)) {
      return 'signOut'
    } else if (url.startsWith(USER_MANAGER.settings.silent_redirect_uri!)) {
      return 'silentRenew'
    } else {
      return 'none'
    }
  }

  const checkExpiration = () => {
    setTimeout(async () => {
      try {
        let user = await authData.manager.getUser()
        if (user?.expires_in && user.expires_in < 900) {
          user = await authData.manager.signinSilent()
          if (user) {
            setAuthData({ ...authData, user: getUserContext(user) })
          } else {
            setAuthData({ ...authData, user: null })
          }
        }
        checkExpiration()
      } catch {
        setAuthData({ ...authData, user: null })
      }
    }, 60000)
  }

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      void (async () => {
        let user: User | null | undefined = null
        const step = authStep()
        try {
          switch (step) {
            case 'signIn': {
              await authData.manager.signinCallback()
              user = await authData.manager.getUser()
              break
            }
            case 'signOut': {
              await authData.manager.signoutCallback()
              user = null
              break
            }
            case 'silentRenew': {
              await authData.manager.signinCallback()
              user = undefined
              break
            }
            case 'none': {
              user = await authData.manager.signinSilent()
              break
            }
          }
        } catch {
          user = null
        } finally {
          if (user === null && MandatorySignIn) {
            await authData.manager.signinRedirect()
          } else if (user || user === null) {
            const result = user ? getUserContext(user) : null
            setAuthData({ manager: USER_MANAGER, user: result })
            console.debug('Sign in result', { user: result })
            Sentry.setUser({ email: user?.profile.email })
            checkExpiration()
            if (step === 'signIn' || step === 'signOut') {
              navigate(Home)
            }
          }
        }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={authData}>{authData.user !== undefined ? props.children : null}</AuthContext.Provider>
  )
}
